import { useState, useEffect, useRef, useCallback } from 'react';
import * as fmt from '../../i18n/format';
import { Loader2, Send, Paperclip, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { supportService } from '../../services/supportService';
import { useAuth } from '../../contexts/AuthContext';
import { useCopy, fill } from '../../i18n';
import { plural } from '../../i18n/format';
import { showToast } from '../common/Toast';
import SatisfactionRating from './SatisfactionRating';
import type { SupportMessage, SupportTicket, SupportTicketStatus } from '../../types/database';

interface TicketConversationProps {
  ticketId: string;
  onStatusChange?: () => void;
}

/**
 * Cor de cada status. A chave é o valor da coluna `status`; o rótulo vem do
 * dicionário (`account.support.statuses`), indexado por ela.
 */
const STATUS_COLORS: Record<SupportTicketStatus, { color: string; bgColor: string }> = {
  open: { color: '#3b82f6', bgColor: 'rgba(59,130,246,0.1)' },
  in_progress: { color: '#f59e0b', bgColor: 'rgba(245,158,11,0.1)' },
  waiting_user: { color: '#8b5cf6', bgColor: 'rgba(139,92,246,0.1)' },
  resolved: { color: '#22c55e', bgColor: 'rgba(34,197,94,0.1)' },
  closed: { color: '#6b7280', bgColor: 'rgba(107,114,128,0.1)' },
};

const TicketConversation = ({ ticketId, onStatusChange }: TicketConversationProps) => {
  const { user } = useAuth();
  const copy = useCopy().account.support;
  const t = copy.conversation;
  const [ticket, setTicket] = useState<SupportTicket | null>(null);
  const [messages, setMessages] = useState<SupportMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const sentMsgIds = useRef<Set<string>>(new Set());

  const loadTicket = useCallback(async () => {
    try {
      const data = await supportService.getTicketWithMessages(ticketId);
      setTicket(data);
      setMessages(data.messages);
    } catch (err) {
      console.error('Error loading ticket:', err);
      showToast(t.loadError, 'error');
    } finally {
      setLoading(false);
    }
  }, [ticketId]);

  useEffect(() => {
    loadTicket();
  }, [loadTicket]);

  // Real-time subscription
  useEffect(() => {
    const channel = supportService.subscribeToTicketMessages(ticketId, (newMsg) => {
      if (sentMsgIds.current.has(newMsg.id)) {
        sentMsgIds.current.delete(newMsg.id);
        return;
      }
      setMessages(prev => {
        if (prev.some(m => m.id === newMsg.id)) return prev;
        return [...prev, newMsg];
      });
    });

    return () => {
      channel.unsubscribe();
    };
  }, [ticketId, user?.id]);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async () => {
    if (!reply.trim() || sending) return;

    const content = reply.trim();
    setReply('');
    setSending(true);

    try {
      const msg = await supportService.addMessage(ticketId, content);
      sentMsgIds.current.add(msg.id);
      setMessages(prev => [...prev, msg]);
      onStatusChange?.();

      // Auto-resize textarea back
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
      }
    } catch (err) {
      console.error('Error sending message:', err);
      showToast(t.sendError, 'error');
      setReply(content); // restore
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleTextareaInput = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setReply(e.target.value);
    // Auto-resize
    const ta = e.target;
    ta.style.height = 'auto';
    ta.style.height = Math.min(ta.scrollHeight, 120) + 'px';
  };

  const handleRated = () => {
    loadTicket();
  };

  const formatTime = (dateStr: string) => {
    const date = new Date(dateStr);
    return fmt.dateTime(date);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 size={24} className="animate-spin text-[var(--color-text-muted)]" />
      </div>
    );
  }

  if (!ticket) return null;

  const statusColor = STATUS_COLORS[ticket.status];
  const isClosed = ticket.status === 'closed' || ticket.status === 'resolved';

  return (
    <div className="flex flex-col h-full">
      {/* Ticket info header */}
      <div className="px-4 py-3 border-b border-[var(--color-border)] bg-[var(--color-bg-secondary)]">
        <h4 className="text-sm font-medium text-[var(--color-text)] mb-1 truncate">
          {ticket.subject}
        </h4>
        <div className="flex items-center gap-2 flex-wrap">
          <span
            className="support-status-badge"
            style={{ color: statusColor.color, background: statusColor.bgColor }}
          >
            {copy.statuses[ticket.status]}
          </span>
          <span className="text-[11px] text-[var(--color-text-muted)]">
            {fill(t.priority, { value: copy.priorities[ticket.priority as keyof typeof copy.priorities] || ticket.priority })}
          </span>
          <span className="text-[11px] text-[var(--color-text-muted)]">
            {formatTime(ticket.created_at)}
          </span>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-3 flex flex-col" style={{ minHeight: '200px', maxHeight: '400px' }}>
        {messages.map(msg => (
          <div
            key={msg.id}
            className={`support-message ${msg.is_admin_reply ? 'admin' : 'user'}`}
          >
            <div className="support-message-bubble">
              {msg.content}
              {/* Attachments */}
              {msg.attachments && msg.attachments.length > 0 && (
                <div className="mt-2 flex flex-col gap-1">
                  {msg.attachments.map(att => (
                    <a
                      key={att.id}
                      href={att.file_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="support-attachment"
                    >
                      <Download size={12} />
                      {att.file_name}
                    </a>
                  ))}
                </div>
              )}
            </div>
            <span className="support-message-meta">
              {msg.sender?.full_name || (msg.is_admin_reply ? t.supportSender : t.youSender)} - {formatTime(msg.created_at)}
            </span>
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      {/* Satisfaction rating (when resolved) */}
      {ticket.status === 'resolved' && !ticket.satisfaction_rating && (
        <SatisfactionRating ticketId={ticket.id} onRated={handleRated} />
      )}

      {ticket.satisfaction_rating && (
        <div className="px-4 py-3 border-t border-[var(--color-border)] bg-[var(--color-bg-secondary)] text-center">
          <span className="text-xs text-[var(--color-text-secondary)]">
            {fill(plural(ticket.satisfaction_rating, t.rated), { count: ticket.satisfaction_rating })}
          </span>
        </div>
      )}

      {/* Reply input */}
      {!isClosed && (
        <div className="support-reply-area">
          <textarea
            ref={textareaRef}
            value={reply}
            onChange={handleTextareaInput}
            onKeyDown={handleKeyDown}
            placeholder={t.replyPlaceholder}
            rows={1}
          />
          <Button
            size="sm"
            className="h-[38px] px-3 self-end"
            onClick={handleSend}
            disabled={!reply.trim() || sending}
          >
            {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
          </Button>
        </div>
      )}

      {isClosed && !ticket.satisfaction_rating && ticket.status === 'closed' && (
        <div className="px-4 py-3 border-t border-[var(--color-border)] text-center">
          <span className="text-xs text-[var(--color-text-muted)]">
            {t.closedNotice}
          </span>
        </div>
      )}
    </div>
  );
};

export default TicketConversation;
